import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import AdminNavbar from '../../Pages/Navbar/AdminNavbar';
import { Button, Dropdown } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import './ReportedCandidateView.css';
import BaseUrl from '../../ApiServices/BaseUrl';

const ReportedCandidateView = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState('Pending');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const { candidateId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await fetch(`${BaseUrl}/api/job-seeker/${candidateId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch profile data');
        }
        const data = await response.json();

        // Construct full URL for the profile photo if it exists
        if (data.profile_photo) {
          data.profile_photo = `${BaseUrl}${data.profile_photo}`;
        }

        // Set verification status from the data
        if (data.verification_status) {
          setVerificationStatus(data.verification_status);
        }

        setProfileData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (candidateId) {
      fetchProfileData();
    }
  }, [candidateId]);

  const safeParseJSON = (jsonString) => {
    if (!jsonString) return [];
    try {
      if (typeof jsonString === 'object') return jsonString;
      const cleanedString = jsonString.replace(/\\"/g, '"');
      return JSON.parse(cleanedString);
    } catch (e) {
      if (Array.isArray(jsonString)) return jsonString;
      if (typeof jsonString === 'string') return [jsonString];
      return [];
    }
  };

  const handleVerificationStatusChange = async (status) => {
    setUpdatingStatus(true);
    try {
      const response = await fetch(`${BaseUrl}/api/job-seeker/${candidateId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          verification_status: status
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update verification status');
      }

      setVerificationStatus(status);

      Swal.fire({
        title: 'Success',
        text: `Candidate status updated to ${status}`,
        icon: 'success',
        confirmButtonText: 'OK'
      });
    } catch (err) {
      console.error("Error updating verification status:", err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to update verification status',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleToggleActivation = async () => {
    const newStatus = verificationStatus === 'In Active' ? 'Verified' : 'In Active';
    await handleVerificationStatusChange(newStatus);
  };

  const handleDeleteUser = async () => {
    // Confirm deletion with user
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (!result.isConfirmed) return;

    try {
      // First delete from job_seeker table
      const jobSeekerResponse = await fetch(`${BaseUrl}/api/job-seeker/${candidateId}`, {
        method: 'DELETE'
      });

      if (!jobSeekerResponse.ok) {
        const errorData = await jobSeekerResponse.json();
        throw new Error(errorData.message || 'Failed to delete from job-seeker table');
      }

      // Then delete from users table - fixed endpoint
      const userId = profileData.user_id;
      const userResponse = await fetch(`${BaseUrl}/${userId}`, {
        method: 'DELETE'
      });

      if (!userResponse.ok) {
        const errorData = await userResponse.json();
        throw new Error(errorData.message || 'Failed to delete from users table');
      }

      // Send rejection email - fixed endpoint and improved error handling
      try {
        const emailResponse = await fetch(`${BaseUrl}/api/send-rejection-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: profileData.email_id,
            firstName: profileData.first_name
          })
        });

        if (!emailResponse.ok) {
          const errorData = await emailResponse.json();
          console.error("Email sending failed:", errorData);
          // Optionally show a warning to admin that email failed
          await Swal.fire({
            title: 'Deleted but email failed',
            text: 'Candidate was deleted but the rejection email failed to send.',
            icon: 'warning',
            confirmButtonText: 'OK'
          });
        }
      } catch (emailError) {
        console.error("Error sending rejection email:", emailError);
        // Don't fail the whole operation if email fails
      }

      // Show success message and navigate back
      await Swal.fire({
        title: 'Deleted!',
        text: 'The candidate has been deleted and rejection sent.',
        icon: 'success',
        confirmButtonText: 'OK'
      });

      navigate('/reportedcandidatesadmin');
    } catch (err) {
      console.error("Error deleting user:", err);
      Swal.fire({
        title: 'Error',
        text: err.message || 'Failed to delete candidate',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleBack = () => {
    navigate('/reportedcandidatesadmin');
  };

  if (loading) {
    return (
      <>
        <AdminNavbar />
        <div className="text-center mt-5">Loading profile data...</div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <AdminNavbar />
        <div className="text-center mt-5 text-danger">Error: {error}</div>
      </>
    );
  }

  if (!profileData) {
    return (
      <>
        <AdminNavbar />
        <div className="text-center mt-5">No profile data found</div>
      </>
    );
  }

  // Parse all JSON fields safely
  const jobPosition = safeParseJSON(profileData.job_position);
  const languages = safeParseJSON(profileData.language);
  const mainSkills = safeParseJSON(profileData.main_skills);
  const cookingSkills = safeParseJSON(profileData.cooking_skills);
  const otherSkills = safeParseJSON(profileData.other_skills);
  const personality = safeParseJSON(profileData.personality);
  const experience = safeParseJSON(profileData.experience);
  const preferredLocations = safeParseJSON(profileData.preffered_job_locations);
  const education = safeParseJSON(profileData.education);

  return (
    <>
      <AdminNavbar />
      <div className="combined-profile-details-container p-3" style={{ marginTop: '55px' }}>
        <div style={{ textAlign: 'center', marginBottom: '10px', marginTop: '15px' }}>
          <Button variant="outline-secondary" onClick={handleBack}>
            ← Back to Reported Candidates
          </Button>
        </div>
        <div className="combined-profile-details-card bg-white shadow rounded-4">
          <hr className="profile-divider" />

          <div className="profile-section p-3">
            <div className='bg-light'>
              <div className="d-flex justify-content-between px-2 mb-2">
                <span
                  className="reported-candidate-badge d-flex align-items-center justify-content-center"
                  style={{
                    minWidth: '90px',
                    height: '38px',
                    borderRadius: '0px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Reported
                </span>
                <div className="d-flex align-items-center gap-2">
                  <Dropdown>
                    <Dropdown.Toggle
                      variant={verificationStatus === 'In Active' ? 'secondary' : 'primary'}
                      size="sm"
                      disabled={updatingStatus}
                      className='reported-candidate-actions-toggle'
                      style={{
                        minWidth: '90px',
                        height: '38px',
                        borderRadius: '0px',
                        fontSize: '14px',
                        color: 'white',
                        fontWeight: '500'
                      }}
                    >
                      {updatingStatus ? 'Updating...' : 'Actions'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className='reported-candidate-dropdown-menu'>
                      <Dropdown.Item
                        onClick={handleToggleActivation}
                        className={`reported-candidate-dropdown-item ${verificationStatus === 'In Active' ? 'reported-candidate-dropdown-item-active' : ''}`}
                      >
                        {verificationStatus === 'In Active' ? 'Make Activate' : 'Make In-Active'}
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={handleDeleteUser}
                        className='reported-candidate-dropdown-item reported-candidate-dropdown-item-delete'
                      >
                        Remove/Delete
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>

              <div className="profile-img-wrapper position-relative mx-auto mb-3">
                {profileData.profile_photo ? (
                  <img
                    src={profileData.profile_photo}
                    alt="Profile"
                    className="profile-img rounded-circle"
                  />
                ) : (
                  <div className="profile-img-placeholder rounded-circle">
                    <i className="fas fa-user"></i>
                  </div>
                )}
              </div>
            </div>

            <div className="profile-info bg-light p-3 rounded-3">
              <h5 className="fw-bold mb-2">
                • Name: {profileData.first_name} {profileData.last_name}, {profileData.age} Years Old
              </h5>
              <p className="mb-1">Current Location: {profileData.current_country}</p>
              <p className="mb-0">Total Experience: {profileData.total_work_experience === 'Fresher (0 Year)' || profileData.total_work_experience === '0' || profileData.total_work_experience === 0 ? 'Fresher' : profileData.total_work_experience}</p>
            </div>
          </div>

          <hr className="profile-divider" />

          <div className="cook-details-section p-3">
            <div className="mb-4">
              <h6 className="fw-bold text-primary d-flex align-items-center">
                ✅ <span className="ms-2">Basic Information</span>
              </h6>
              <div className="cook-info-box mt-2">
                <ul className="list-group list-group-flush">
                  <li className="list-group-item cook-info-item">
                    <span className="text-success">•</span> Gender: {profileData.gender}
                  </li>
                  {/* <li className="list-group-item cook-info-item">
                    <span className="text-success">•</span> Religion: {profileData.religion}
                  </li> */}
                  <li className="list-group-item cook-info-item">
                    <span className="text-success">•</span> Marital Status: {profileData.marital_status}
                  </li>
                  <li className="list-group-item cook-info-item">
                    <span className="text-success">•</span> Nationality: {profileData.nationality}
                  </li>
                  <li className="list-group-item cook-info-item">
                    <span className="text-success">•</span> Qualification: {education[0]?.level || 'Not specified'}
                  </li>
                  <li className="list-group-item cook-info-item">
                    <span className="text-success">•</span> Languages: {languages.map(lang => lang.language).join(', ')}
                  </li>
                </ul>
              </div>
            </div>

            {languages.map((lang, idx) => (
              <div className="mb-3" key={idx}>
                <h6 className="fw-bold text-primary d-flex align-items-center">
                  ✅ <span className="ms-2">{lang.language} Language</span>
                </h6>
                <div className="d-flex flex-wrap gap-2 mt-3 mx-4">
                  {lang.speaking_level && <span className="language-tag">Speak: {lang.speaking_level}</span>}
                  {lang.can_read && <span className="language-tag">Read</span>}
                  {lang.can_write && <span className="language-tag">Write</span>}
                </div>
              </div>
            ))}

            <div>
              <h6 className="fw-bold text-primary d-flex align-items-center">
                ✅ <span className="ms-2">Main Skills</span>
              </h6>
              <div className="d-flex flex-wrap gap-2 mt-3 mx-3">
                {mainSkills.map((skill, idx) => (
                  <span key={idx} className="skill-tag">{skill}</span>
                ))}
              </div>
            </div>
          </div>

          <hr className="profile-divider" />

          <div className="skills-experience-section p-3">
            <div className="mb-4">
              <h6 className="fw-bold text-primary d-flex align-items-center">
                ✅ <span className="ms-2">Cooking Skills</span>
              </h6>
              <div className="d-flex flex-wrap gap-2 mt-3 mx-3">
                {cookingSkills.map((item, index) => (
                  <span key={index} className="skill-tag">{item}</span>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <h6 className="fw-bold text-primary d-flex align-items-center">
                ✅ <span className="ms-2">Other Skills</span>
              </h6>
              <div className="d-flex flex-wrap gap-2 mt-3 mx-3">
                {otherSkills.map((item, index) => (
                  <span key={index} className="skill-tag">{item}</span>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <h6 className="fw-bold text-primary d-flex align-items-center">
                ✅ <span className="ms-2">Personality</span>
              </h6>
              <div className="d-flex flex-wrap gap-2 mt-3 mx-3">
                {personality.map((trait, index) => (
                  <span key={index} className="trait-tag">{trait}</span>
                ))}
              </div>
            </div>

            <div>
              <h6 className="fw-bold text-primary d-flex align-items-center">
                ✅ <span className="ms-2">Work Experience</span>
              </h6>
              {experience.length > 0 ? (
                experience.map((exp, expIdx) => {
                  const countryDisplay = Array.isArray(exp.country)
                    ? exp.country.join(', ')
                    : exp.country || '';

                  const jobPositionDisplay = Array.isArray(exp.job_position)
                    ? exp.job_position.join(', ')
                    : exp.job_position || '';

                  const dutiesDisplay = Array.isArray(exp.house_duties)
                    ? exp.house_duties.join(', ')
                    : exp.house_duties || '';

                  return (
                    <div className="bg-light p-3 rounded mt-3 mx-3" key={expIdx}>
                      <ul className="mb-2 ps-3">
                        <li>{exp.join_month} {exp.join_year} - {exp.exit_month} {exp.exit_year}</li>
                        <li>{jobPositionDisplay} {countryDisplay}</li>
                        <li>{exp.employer_nationality} Family</li>
                        <li>Duties: {dutiesDisplay}</li>
                      </ul>
                      {profileData.reference_letter && (
                        <p className="text-success fw-bold mb-0 ps-3">• I have a Reference letter</p>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="bg-light p-3 rounded mt-3 mx-3">
                  <p>No work experience listed</p>
                </div>
              )}
            </div>
          </div>

          <hr className="profile-divider" />

          <div className="salary-preference-section p-3">
            <div className="mb-4">
              <h6 className="fw-bold text-primary d-flex align-items-center">
                ✅ <span className="ms-2">Salary Expectations</span>
              </h6>
              <div className="custom-value-box px-3 py-2 bg-light rounded text-secondary mb-2">
                Salary in Qatar: {profileData.expected_monthly_salary_qar} QR
              </div>
              <div className="custom-value-box px-3 py-2 bg-light rounded text-secondary mb-2">
                Salary in USD: US $ {profileData.expected_monthly_salary_usd}
              </div>
            </div>

            <div className="mb-4">
              <h6 className="fw-bold text-primary d-flex align-items-center">
                ✅ <span className="ms-2">Preferred Locations</span>
              </h6>
              <div className="d-flex flex-wrap gap-2">
                {preferredLocations.map((loc, index) => (
                  <span key={index} className="location-pill px-3 py-2 bg-light rounded">
                    {loc}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <h6 className="fw-bold text-primary d-flex align-items-center">
                ✅ <span className="ms-2">About Me</span>
              </h6>
              <div className="about-me-box p-2 border rounded">
                {profileData.resume_description}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReportedCandidateView;